import React from "react";
import { Stack, List, Divider } from "@mui/material";

import { useSidebar, SidebarProvider } from "./sidebar-context";
import { DRAWER_WIDTH } from "../constants";
import { SidebarDrawer } from "./sidebar-drawer";
import { SidebarHeader } from "./sidebar-header";
import { SidebarItem } from "./sidebar-item";
import { SidebarFooter } from "./sidebar-footer";
import { routes } from "util/routes";
import { DashboardFilled } from "components/icons/filled/dashboard";
import { Protected } from "features/auth/components/protected";
import { permissions } from "features/auth/permissions";
import { useTranslation } from "hooks/use-translation";
import { ConnectFilled } from "components/icons/filled/connect";
import { SidebarSubItem } from "./sidebar-sub-item";
import { EngageFilled } from "components/icons/filled/engage";
import { AdminSettingsFilled } from "components/icons/filled/admin-settings";
import { EducateFilled } from "components/icons/filled/educate";
import { AnnouncementFilled } from "components/icons/filled/announcement";
import { CompanyFilled } from "components/icons/filled/company";
import { SettingsFilled } from "components/icons/filled/settings";
import { DownloadOutlined } from "components/icons/outlined/download";

export function Sidebar() {
  return (
    <SidebarProvider>
      <SideBarContent />
    </SidebarProvider>
  );
}

function SideBarContent() {
  const { t } = useTranslation();
  const { isSidebarOpen, toggleSidebar } = useSidebar();

  return (
    <SidebarDrawer
      variant="permanent"
      open={isSidebarOpen}
      onMouseEnter={() => toggleSidebar(true)}
      onMouseLeave={() => toggleSidebar(false)}
    >
      <Stack
        direction="column"
        flex={1}
        sx={{ width: DRAWER_WIDTH }}
        role="presentation"
      >
        <SidebarHeader />
        <Stack direction="column" spacing={1.25} flex={1} sx={{ py: 2 }}>
          <List
            disablePadding
            sx={{ "& > :not(.MuiCollapse-root)": { mt: 1.25 } }}
          >
            <SidebarItem
              isLink
              href={routes.homePage()}
              icon={DashboardFilled}
              title={t("DIC_SIDE_BAR_DASHBOARD")}
            />
            <Connect />
            <Survey />
            <ELearning />
            <CompanyPost />
            <MyWovo />
            <Settings />
            <Administration />
            <Protected permissions={[permissions.canViewDownloads]}>
              <SidebarItem
                href={routes.downloads()}
                icon={DownloadOutlined}
                isExternal
                isLink
                title={t("DIC_SIDE_BAR_DOWNLOADS")}
              />
            </Protected>
          </List>
        </Stack>
        <Divider />
        <SidebarFooter />
      </Stack>
    </SidebarDrawer>
  );
}

function Connect() {
  const { t } = useTranslation();
  return (
    <SidebarItem icon={ConnectFilled} title={t("DIC_SIDE_BAR_CONNECT")}>
      <Protected permissions={[permissions.canViewConnectReports]}>
        <SidebarSubItem
          href={routes.connect.reports()}
          title={t("DIC_SIDE_BAR_CONNECT_REPORTS")}
        />
      </Protected>
      <Protected permissions={[permissions.canCreateManualCase]}>
        <SidebarSubItem
          href={routes.connect.manualCaseCreation()}
          title={t("DIC_SIDE_BAR_MANUAL_CASE")}
          isExternal
        />
      </Protected>
      <Protected permissions={[permissions.canViewPaySlip]}>
        <SidebarSubItem
          href={routes.connect.payslipList()}
          title={t("DIC_SIDE_BAR_PAY_SLIP")}
        />
      </Protected>
    </SidebarItem>
  );
}

function Survey() {
  const { t } = useTranslation();
  return (
    <SidebarItem icon={EngageFilled} title={t("DIC_SIDE_BAR_SURVEY")}>
      <Protected permissions={[permissions.canManageSurveys]}>
        <SidebarSubItem
          href={routes.survey.home()}
          title={t("DIC_SIDE_BAR_SURVEY_HOME")}
        />
      </Protected>
      <Protected permissions={[permissions.canManageQuestionnaires]}>
        <SidebarSubItem
          href={routes.survey.templates()}
          title={t("DIC_SIDE_BAR_SURVEY_TEMPLATES")}
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.survey.questionTypes()}
          title={t("DIC_SIDE_BAR_SURVEY_QUESTION_TYPES")}
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.survey.questionBank()}
          title={t("DIC_SIDE_BAR_SURVEY_QUESTION_BANK")}
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.survey.reportingCategories()}
          title={t("DIC_SIDE_BAR_SURVEY_REPORTING_CATEGORIES")}
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.survey.surveyBank()}
          title={t("DIC_SIDE_BAR_SURVEY_BANK")}
          isExternal
        />
      </Protected>
    </SidebarItem>
  );
}

function ELearning() {
  const { t } = useTranslation();
  return (
    <SidebarItem icon={EducateFilled} title={t("DIC_SIDE_BAR_E_LEARNING")}>
      <Protected permissions={[permissions.canViewELearningReports]}>
        <SidebarSubItem
          href={routes.eLearning.reports()}
          title={t("DIC_SIDE_BAR_E_LEARNING_REPORTS")}
        />
      </Protected>
    </SidebarItem>
  );
}

function CompanyPost() {
  const { t } = useTranslation();
  return (
    <SidebarItem
      icon={AnnouncementFilled}
      title={t("DIC_SIDE_BAR_COMPANY_POST")}
    >
      <Protected permissions={[permissions.canViewCompanyPostReports]}>
        <SidebarSubItem
          href={routes.companyPost.reports()}
          title={t("DIC_SIDE_BAR_COMPANY_POST_RAW_DATA")}
        />
      </Protected>
    </SidebarItem>
  );
}

function MyWovo() {
  const { t } = useTranslation();
  return (
    <SidebarItem icon={CompanyFilled} title={t("DIC_SIDE_BAR_MY_WOVO")}>
      <Protected permissions={[permissions.canManageAccounts]}>
        <SidebarSubItem
          href={routes.myWovo.accountInfo()}
          title={t("DIC_SIDE_BAR_ACCOUNT_INFO")}
          isExternal
        />
      </Protected>
      <Protected permissions={[permissions.canManageWorkerContacts]}>
        <SidebarSubItem
          href={routes.myWovo.workerContacts()}
          title={t("DIC_SIDE_BAR_WORKER_CONTACTS")}
        />
      </Protected>
      <Protected permissions={[permissions.canManageUsers]}>
        <SidebarSubItem
          href={routes.administration.users()}
          title={t("DIC_SIDE_BAR_ADMINISTRATION_DASHBOARD_USERS")}
          isExternal
        />
      </Protected>
    </SidebarItem>
  );
}

function Settings() {
  const { t } = useTranslation();
  return (
    <SidebarItem icon={SettingsFilled} title={t("DIC_SIDE_BAR_SETTINGS")}>
      <Protected>
        <SidebarSubItem
          href={routes.adminSettings.localisation()}
          title={t("DIC_SIDE_BAR_SETTINGS_TRANSLATIONS")}
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.adminSettings.manageTranslations()}
          title={t("DIC_SIDE_BAR_SETTINGS_MANAGE_TRANSLATIONS")}
          isExternal
        />
      </Protected>
    </SidebarItem>
  );
}

function Administration() {
  const { t } = useTranslation();
  return (
    <SidebarItem
      icon={AdminSettingsFilled}
      title={t("DIC_SIDE_BAR_ADMINISTRATION")}
      isLink={false}
    >
      <Protected permissions={[permissions.canManageRoles]}>
        <SidebarSubItem
          href={routes.administration.roles()}
          title={t("DIC_SIDE_BAR_ADMINISTRATION_ROLES")}
          isExternal
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.administration.adminUsers()}
          title={t("DIC_SIDE_BAR_ADMINISTRATION_ADMIN_USERS")}
          isExternal
        />
      </Protected>
      <Protected>
        <SidebarSubItem
          href={routes.administration.lessonAssociation()}
          title={t("DIC_SIDE_BAR_ADMINISTRATION_LESSON_ASSOCIATION")}
          isExternal
        />
      </Protected>
    </SidebarItem>
  );
}
